import { FC, ReactElement } from 'react'
import styled from 'styled-components'

const changeArrows: { [key: string]: string } = {
  right:
    'M31.0672,1.1723l15.5877,15.5877c.4393,.4421,.4393,1.156,0,1.5981l-15.588,15.588c-1.5451,1.5451-4.0503,1.5451-5.5955,0s-1.5451-4.0503,0-5.5955l6.8348-6.8348-28.3646,.0004C1.7562,21.5077-.0085,19.7292,0,17.5438c.0085-2.1733,1.7682-3.933,3.9415-3.9415l28.3678-.0044-6.8348-6.8348c-1.5516-1.5364-1.564-4.0397-.0276-5.5913,1.5364-1.5516,4.0397-1.564,5.5913-.0276l.0282,.0282h.0007Z',
  down: 'M43.4224,13.67V35.7144a1.1336,1.1336,0,0,1-1.13,1.13H20.2476a3.9566,3.9566,0,1,1,0-7.9132h9.6658L7.735,6.7534a3.957,3.957,0,0,1,5.596-5.596L35.5145,23.3346V13.6689a3.9537,3.9537,0,1,1,7.9073.0008Z',
  up: 'M20.2477,0H42.292a1.1336,1.1336,0,0,1,1.13,1.13V23.1748a3.9566,3.9566,0,0,1-7.9132,0V13.509L13.331,35.6874a3.957,3.957,0,0,1-5.596-5.596L29.9123,7.9079H20.2465A3.9537,3.9537,0,1,1,20.2473.0006Z',
}

function prettifyNumber(num: number, locale: 'fr' | 'de') {
  return Intl.NumberFormat(locale + '-CH').format(num)
}

interface ValueBoxProps {
  label: string
  value: number
  percentage?: number | undefined
  value7days?: number
  colorMode: 'dark' | 'light'
  color?: string
  bgcolor?: string
  prettify?: boolean
  locale?: 'fr' | 'de'
}

const ValueBox: FC<ValueBoxProps> = ({
  label,
  value,
  value7days,
  colorMode = 'dark',
  color,
  bgcolor,
  percentage,
  prettify = false,
  locale = 'fr',
}): ReactElement => {
  let arrowDirection = 'right'
  let percentageStr = ''
  if (percentage !== undefined) {
    if (Math.abs(percentage) > 5) {
      if (percentage > 0) {
        arrowDirection = 'up'
      } else if (percentage < 0) {
        arrowDirection = 'down'
      }
    }
    percentageStr =
      (percentage > 0 ? '+' : '') + Math.round(percentage).toString()
  }
  return (
    <Box color={colorMode}>
      <Label>{label}</Label>
      <MainValue
        color={bgcolor}
        style={{
          flexBasis: percentage !== undefined ? 'calc(100% - 65px)' : '100%',
        }}
      >
        <Value color={color}>
          {prettify ? prettifyNumber(value, locale) : value}
        </Value>
      </MainValue>
      {percentage !== undefined && (
        <Trend color={bgcolor}>
          <Percentage>
            <Arrow>
              <svg viewBox={'0 -4 50 50'}>
                <path
                  style={{ fill: color }}
                  d={changeArrows[arrowDirection]}
                />
              </svg>
            </Arrow>
            <Value color={color}>{percentageStr}%</Value>
          </Percentage>
        </Trend>
      )}
    </Box>
  )
}

const Box = styled.div`
  display: flex;
  background: ${(props) => (props.color === 'dark' ? '#232323' : '#fff')};
  box-shadow: 0 0 10px 0 rgb(0 0 0 / 15%);
  border-radius: 4px;
  padding: 6px;
  flex: 0 0 calc(50% - 22px);
  flex-wrap: wrap;
  flex-shrink: 0;
  grid-gap: 5px;
  gap: 5px;
  font-family: var(--font-plex);
`

const Label = styled.div`
  flex: 0 1 calc(100% - 65px);
  font-size: 0.9em;
`

const MainValue = styled.div`
  border-radius: 4px;
  background-color: ${(props) => props.color || '#ffe462'};
  font-weight: bold;
  min-height: 60px;
  align-items: center;
  justify-content: center;
`

const Value = styled.div`
  color: ${(props) => props.color || '000'};
  display: flex;
  flex: 0 0 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`

const Trend = styled.div`
  border-radius: 4px;
  background-color: ${(props) => props.color || '#ffe462'};
`

const Percentage = styled.div`
  flex: 0 0 7%;
`

const Arrow = styled.div`
  height: 25px;
  margin-top: 10px;
  width: 60px;
  display: flex;
  justify-content: center;
  align-items: bottom;
`

export default ValueBox
